import {
  ExpandMore as ExpandMoreIcon,
  Assignment as PlanningIcon,
  Schedule as ScheduleIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { useActiveUserWorkItems } from "../../../hooks/useActiveUserWorkItems";
import {
  WorkItemList,
  WorkItemStateEnum,
} from "../../../store/services/timelineApiRTK";
import {
  WORK_ITEM_CATEGORIES,
  WorkItemCategory,
} from "../../../utils/workItemUtils";
import { isOverdue } from "../../../utils/activityHelpers";
import ActivityLink from "../../activity-link/ActivityLink";
import WorkItemListItem from "../../workitem/WorkItemListItem";

const TodaysActivities = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: workItems = [], isLoading } = useActiveUserWorkItems({
    state: WorkItemStateEnum.Active,
  });
  const { planningActivities, scheduledActivities } = React.useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const endOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1
    );

    const activeItems = workItems.filter((item) => {
      if (item.State !== WorkItemStateEnum.Active) return false;
      if (!item.ScheduledTo) return false;

      const scheduledDate = new Date(item.ScheduledTo);
      if (isNaN(scheduledDate.getTime())) return false;

      return scheduledDate >= startOfToday && scheduledDate < endOfToday;
    });

    // Separera planering från uppföljning baserat på WorkItemTypeEnum
    const planningTypes = WORK_ITEM_CATEGORIES[WorkItemCategory.COMMUNICATION]; // Email, PhoneCall, SMS, DigitalMeeting, PhysicalMeeting
    const followUpTypes = WORK_ITEM_CATEGORIES[WorkItemCategory.FOLLOWUP]; // FollowUp* types

    const planning = activeItems.filter((item) =>
      planningTypes.includes(item.Type)
    );
    const scheduled = activeItems
      .filter((item) => followUpTypes.includes(item.Type))
      .sort((a, b) => {
        if (!a.ScheduledTo || !b.ScheduledTo) return 0;
        return (
          new Date(a.ScheduledTo).getTime() - new Date(b.ScheduledTo).getTime()
        );
      });

    return {
      planningActivities: planning,
      scheduledActivities: scheduled.slice(0, 5), // Max 5 schemalagda
    };
  }, [workItems]);

  if (isLoading) {
    return (
      <Card elevation={0} sx={{ border: "1px solid", borderColor: "grey.200" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="medium">
            {isMobile ? "Idag" : "Dagens aktiviteter"}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {[1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width="100%"
                height={60}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  const totalActivities =
    planningActivities.length + scheduledActivities.length;

  return (
    <Card elevation={0} sx={{ border: "1px solid", borderColor: "grey.200" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <TodayIcon color="primary" />
          <Typography variant="h6" fontWeight="medium">
            {isMobile ? "Idag" : "Dagens aktiviteter"}
            {totalActivities > 0 && (
              <Chip
                label={totalActivities}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {totalActivities === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <ScheduleIcon
                sx={{
                  fontSize: 48,
                  mb: 1,
                  opacity: 0.5,
                  color: "primary.main",
                }}
              />
              <Typography
                variant="body2"
                color="primary.main"
                fontWeight="medium"
              >
                Inga aktiviteter idag! 🎉
              </Typography>
              <Typography variant="caption" color="text.secondary">
                En lugn dag för dig
              </Typography>
            </Box>
          ) : (
            <>
              {/* Planning Activities Section */}
              {planningActivities.length > 0 && (
                <PlanningSection
                  activities={planningActivities}
                  isMobile={isMobile}
                />
              )}

              {/* Scheduled Activities Section */}
              {scheduledActivities.length > 0 && (
                <ScheduledSection
                  activities={scheduledActivities}
                  isMobile={isMobile}
                />
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TodaysActivities;

// Planning Activities Component
interface PlanningSectionProps {
  activities: WorkItemList[];
  isMobile: boolean;
}

const PlanningSection: React.FC<PlanningSectionProps> = ({
  activities,
  isMobile,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "warning.main",
          "&:before": { display: "none" },
          borderRadius: 2,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <PlanningIcon sx={{ color: "warning.dark" }} />
            <Typography
              variant="subtitle1"
              fontWeight="medium"
              sx={{ color: "warning.dark" }}
            >
              {isMobile ? "Planering" : "Att planera idag"}
            </Typography>
            <Chip
              label={activities.length}
              size="small"
              sx={{
                bgcolor: "warning.main",
                color: "white",
                fontWeight: "bold",
              }}
            />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: "warning.dark" }}>
              Schemalägg dessa aktiviteter
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {activities.map((item) => (
              <ActivityLink
                key={item.Id}
                to={`/activity/${item.Id}`}
                style={{ textDecoration: "none", color: "inherit" }}
                customerNo={item.Customer.Id}
                workItemNo={item.Id}
              >
                <WorkItemListItem
                  workItemType={item.Type}
                  compactWithCustomerInfo
                  title={item.Name}
                  description={item.Description}
                  date={item.ScheduledTo}
                  customerName={item.Customer?.Name || "Ingen kund"}
                  showAction={false}
                  isCompleted={item.State === WorkItemStateEnum.Closed}
                  isActive={item.State === WorkItemStateEnum.Active}
                  isOverdue={isOverdue(item)}
                  compact
                  resultData={item.Result}
                  workId={item.WorkId}
                />
              </ActivityLink>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// Scheduled Activities Component
interface ScheduledSectionProps {
  activities: WorkItemList[];
  isMobile: boolean;
}

const ScheduledSection: React.FC<ScheduledSectionProps> = ({
  activities,
  isMobile,
}) => {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <ScheduleIcon sx={{ color: "primary.main" }} />
        <Typography
          variant="subtitle1"
          fontWeight="medium"
          sx={{ color: "primary.main" }}
        >
          {isMobile ? "Schemalagt" : "Schemalagda uppföljningar"}
        </Typography>
        <Chip label={activities.length} size="small" color="primary" />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {activities.map((item) => (
          <ActivityLink
            key={item.Id}
            to={`/activity/${item.Id}`}
            style={{ textDecoration: "none", color: "inherit" }}
            customerNo={item.Customer.Id}
            workItemNo={item.Id}
          >
            <WorkItemListItem
              workItemType={item.Type}
              title={item.Name}
              description={item.Description}
              date={item.ScheduledTo}
              customerName={item.Customer?.Name || "Ingen kund"}
              showAction={false}
              isCompleted={item.State === WorkItemStateEnum.Closed}
              isActive={item.State === WorkItemStateEnum.Active}
              isOverdue={isOverdue(item)}
              workId={item.WorkId}
            />
          </ActivityLink>
        ))}
      </Box>
    </Box>
  );
};
